import React, { useEffect } from 'react';

// Base critical CSS that should always be inlined
const baseCriticalCSS = `
  /* Enhanced critical styles that should load immediately */
  * {
    box-sizing: border-box;
    margin: 0;
    padding: 0;
  }
  
  body {
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, 'Open Sans', 'Helvetica Neue', sans-serif;
    line-height: 1.5;
    color: #333;
    background-color: #fff;
    text-rendering: optimizeSpeed;
  }
  
  a {
    color: #0070f3;
    text-decoration: none;
    transition: color 0.2s;
  }
  
  a:hover {
    text-decoration: underline;
  }
  
  img {
    max-width: 100%;
    height: auto;
    display: block;
    font-style: italic;
    vertical-align: middle;
  }
  
  /* Basic layout structure */
  .container {
    width: 100%;
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 1rem;
  }
  
  /* Header styles */
  header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 1rem 0;
    background-color: #fff;
    box-shadow: 0 2px 4px rgba(0,0,0,0.05);
    position: relative;
    z-index: 1000;
  }
  
  /* Navigation styles */
  nav ul {
    display: flex;
    list-style: none;
  }
  
  nav ul li {
    margin-left: 1.5rem;
  }
  
  /* Basic button styles */
  .btn {
    display: inline-block;
    background-color: #0070f3;
    color: white;
    padding: 0.5rem 1rem;
    border-radius: 4px;
    cursor: pointer;
    font-weight: 500;
    border: none;
    transition: background-color 0.3s ease, transform 0.2s ease;
  }
  
  .btn:hover {
    background-color: #0051bb;
    transform: translateY(-2px);
  }
  
  /* Hero section basic styles */
  .hero-section {
    padding: 4rem 0;
    background: linear-gradient(135deg, #f5f7fa 0%, #e4e5e6 100%);
    position: relative;
  }
  
  .hero-section h1 {
    font-size: 2.5rem;
    margin-bottom: 1rem;
    line-height: 1.2;
  }
  
  /* Footer basic styles */
  footer {
    background-color: #f9f9f9;
    padding: 2rem 0;
    border-top: 1px solid #eaeaea;
    margin-top: auto;
  }
  
  /* Loading spinner */
  .page-loader {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 100vh;
    width: 100%;
  }
  
  .spinner-border {
    display: inline-block;
    width: 2rem;
    height: 2rem;
    border: 0.25em solid currentColor;
    border-right-color: transparent;
    border-radius: 50%;
    animation: spinner-border .75s linear infinite;
  }
  
  @keyframes spinner-border {
    to { transform: rotate(360deg); }
  }
  
  /* Accessibility utilities */
  .visually-hidden, .sr-only {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
  }
  
  /* Focus styles for accessibility */
  a:focus, button:focus, input:focus, select:focus, textarea:focus {
    outline: 3px solid #0070f3;
    outline-offset: 2px;
  }
`;

interface CriticalCSSProps {
  additionalCSS?: string;
  pageSpecific?: boolean;
}

/**
 * CriticalCSS component to inline critical CSS in the head
 * This reduces render-blocking resources and speeds up First Contentful Paint
 */
const CriticalCSS: React.FC<CriticalCSSProps> = ({ 
  additionalCSS = '',
  pageSpecific = false
}) => {
  useEffect(() => {
    // Create style element for critical CSS
    const style = document.createElement('style');
    style.setAttribute('type', 'text/css');
    style.setAttribute('data-critical', pageSpecific ? 'page-specific' : 'base');
    style.innerHTML = baseCriticalCSS + additionalCSS;
    
    // Insert at the top of the head to ensure it's loaded first
    const head = document.head;
    
    // If this is page-specific CSS, check for existing base critical CSS
    if (pageSpecific) {
      const existingCritical = document.querySelector('style[data-critical="page-specific"]');
      if (existingCritical) {
        existingCritical.remove();
      }
      head.appendChild(style);
    } else {
      // For base CSS, insert at the beginning
      const existingBaseCritical = document.querySelector('style[data-critical="base"]');
      if (!existingBaseCritical) {
        head.insertBefore(style, head.firstChild);
      }
    }
    
    // Cleanup function to remove the style when component unmounts
    return () => {
      if (pageSpecific) {
        const criticalStyle = document.querySelector('style[data-critical="page-specific"]');
        if (criticalStyle) {
          criticalStyle.remove();
        }
      }
    };
  }, [additionalCSS, pageSpecific]);
  
  // Empty render as this only affects the document head
  return null;
};

export default CriticalCSS; 